import React, { useState } from 'react';
import { motion } from 'framer-motion';

// Layout components
import Layout from '../../../components/layout/Layout';
import Section from '../../../components/layout/Section';
import Container from '../../../components/layout/Container';

// UI Components
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import Badge from '../../../components/ui/Badge';

/**
 * Guides & Templates page component
 */
const GuidesPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Mock data for guides and templates
  const categories = [
    'All',
    'Implementation Guides',
    'Business Templates',
    'Training Materials',
    'Best Practices',
    'Checklists'
  ];

  const resources = [
    {
      id: '1',
      title: 'AI Implementation Roadmap',
      description: 'A comprehensive guide to implementing AI solutions in your organization, from assessment to deployment.',
      category: 'Implementation Guides',
      type: 'Guide',
      downloadCount: '2.5k',
      pages: 45,
      format: 'PDF',
      image: '/api/placeholder/300/200',
      featured: true,
    },
    {
      id: '2',
      title: 'Project Charter Template',
      description: 'Kickstart your AI projects with this professional template that outlines objectives, scope, and success metrics.',
      category: 'Business Templates',
      type: 'Template',
      downloadCount: '1.8k',
      pages: 12,
      format: 'DOCX',
      image: '/api/placeholder/300/200',
      featured: false,
    },
    {
      id: '3',
      title: 'AI Training Workshop Agenda',
      description: 'A complete workshop agenda and materials for training your team on AI fundamentals and practical applications.',
      category: 'Training Materials',
      type: 'Template',
      downloadCount: '3.2k',
      pages: 25,
      format: 'PPT + PDF',
      image: '/api/placeholder/300/200',
      featured: true,
    },
    {
      id: '4',
      title: 'Data Privacy & AI Compliance Guide',
      description: 'Navigate the regulatory landscape with this guide covering GDPR, CCPA, and other privacy considerations for AI.',
      category: 'Best Practices',
      type: 'Guide',
      downloadCount: '1.5k',
      pages: 35,
      format: 'PDF',
      image: '/api/placeholder/300/200',
      featured: false,
    },
    {
      id: '5',
      title: 'ROI Calculation Spreadsheet',
      description: 'Calculate the return on investment for your AI initiatives with this comprehensive Excel template.',
      category: 'Business Templates',
      type: 'Template',
      downloadCount: '2.1k',
      pages: 1,
      format: 'XLSX',
      image: '/api/placeholder/300/200',
      featured: false,
    },
    {
      id: '6',
      title: 'AI Readiness Assessment Checklist',
      description: 'Evaluate your organization\'s readiness for AI implementation with this detailed checklist.',
      category: 'Checklists',
      type: 'Checklist',
      downloadCount: '4.1k',
      pages: 8,
      format: 'PDF',
      image: '/api/placeholder/300/200',
      featured: true,
    },
    {
      id: '7',
      title: 'Machine Learning Model Documentation Template',
      description: 'Document your ML models professionally with this comprehensive template covering all aspects.',
      category: 'Business Templates',
      type: 'Template',
      downloadCount: '1.2k',
      pages: 18,
      format: 'DOCX',
      image: '/api/placeholder/300/200',
      featured: false,
    },
    {
      id: '8',
      title: 'AI Ethics Framework',
      description: 'Establish ethical guidelines for AI development and deployment in your organization.',
      category: 'Best Practices',
      type: 'Guide',
      downloadCount: '1.7k',
      pages: 28,
      format: 'PDF',
      image: '/api/placeholder/300/200',
      featured: false,
    },
  ];

  const filteredResources = selectedCategory === 'All' 
    ? resources 
    : resources.filter(resource => resource.category === selectedCategory);

  const featuredResources = resources.filter(resource => resource.featured);

  return (
    <Layout>
      {/* Hero Section */}
      <Section className="relative bg-gradient-to-br from-primary via-secondary to-accent overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 border border-white/20 rounded-full"></div>
          <div className="absolute top-32 right-20 w-24 h-24 border border-white/20 rounded-full"></div>
          <div className="absolute bottom-20 left-1/4 w-40 h-40 border border-white/10 rounded-full"></div>
        </div>
        
        <Container className="relative z-10 py-24 md:py-32">
          <div className="max-w-4xl mx-auto text-center text-white">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-6">
                <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18.1 7.5 18.1s3.332.477 4.5 1.1m0-13C13.168 5.477 14.754 5 16.5 5s3.332.477 4.5 1.253v13C19.832 18.477 18.246 18.1 16.5 18.1s-3.332.477-4.5 1.1" />
                </svg>
              </div>
            </motion.div>
            
            <motion.h1 
              className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Guides & 
              <span className="block bg-gradient-to-r from-cyan-300 to-white bg-clip-text text-transparent">
                Templates
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-xl md:text-2xl mb-12 text-white/90 font-light"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Practical resources to accelerate your AI journey
              <br className="hidden md:block" />
              with proven templates and comprehensive guides
            </motion.p>
          </div>
        </Container>
      </Section>

      {/* Featured Resources Section */}
      <Section className="py-20 bg-white">
        <Container>
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-neutral-900">
              Featured Resources
            </h2>
            <p className="text-lg text-neutral-600 leading-relaxed max-w-3xl mx-auto">
              Our most popular guides and templates, trusted by thousands of professionals worldwide
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredResources.map((resource, index) => (
              <motion.div
                key={resource.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="group"
              >
                <Card className="h-full bg-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden">
                  {/* Image */}
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={resource.image} 
                      alt={resource.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge 
                        variant={resource.type === 'Guide' ? 'primary' : resource.type === 'Template' ? 'secondary' : 'success'}
                      >
                        {resource.type}
                      </Badge>
                      <Badge variant="outline">Featured</Badge>
                    </div>
                    
                    <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
                      {resource.title}
                    </h3>
                    
                    <p className="text-neutral-600 mb-4 leading-relaxed">
                      {resource.description}
                    </p>
                    
                    {/* Metadata */}
                    <div className="flex items-center justify-between text-sm text-neutral-500 mb-4">
                      <span>{resource.downloadCount} downloads</span>
                      <span>{resource.pages} {resource.pages > 1 ? 'pages' : 'page'}</span>
                      <span>{resource.format}</span>
                    </div>
                    
                    <Button 
                      variant="primary" 
                      fullWidth 
                      className="group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-secondary transition-all duration-300"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H5a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download Free
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* All Resources Section */}
      <Section className="py-20 bg-neutral-50">
        <Container>
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-neutral-900">
              All Resources
            </h2>
            <p className="text-lg text-neutral-600 leading-relaxed max-w-3xl mx-auto">
              Browse our complete collection of guides and templates by category
            </p>
          </motion.div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className="transition-all duration-300"
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Resources Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {filteredResources.map((resource, index) => (
              <motion.div
                key={resource.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="group"
              >
                <Card className="h-full bg-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden">
                  {/* Image */}
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={resource.image} 
                      alt={resource.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge 
                        variant={resource.type === 'Guide' ? 'primary' : resource.type === 'Template' ? 'secondary' : 'success'}
                      >
                        {resource.type}
                      </Badge>
                      {resource.featured && (
                        <Badge variant="outline">Featured</Badge>
                      )}
                    </div>
                    
                    <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
                      {resource.title}
                    </h3>
                    
                    <p className="text-neutral-600 mb-4 leading-relaxed">
                      {resource.description}
                    </p>
                    
                    {/* Metadata */}
                    <div className="flex items-center justify-between text-sm text-neutral-500 mb-4">
                      <span>{resource.downloadCount} downloads</span>
                      <span>{resource.pages} {resource.pages > 1 ? 'pages' : 'page'}</span>
                      <span>{resource.format}</span>
                    </div>
                    
                    <Button 
                      variant="primary" 
                      fullWidth 
                      className="group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-secondary transition-all duration-300"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H5a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download Free
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* CTA Section */}
      <Section className="py-20 bg-gradient-to-r from-neutral-900 to-neutral-800 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-4 h-4 bg-white rounded-full"></div>
          <div className="absolute top-20 right-20 w-3 h-3 bg-white rounded-full"></div>
          <div className="absolute bottom-20 left-1/4 w-5 h-5 bg-white rounded-full"></div>
          <div className="absolute bottom-10 right-1/3 w-4 h-4 bg-white rounded-full"></div>
        </div>
        
        <Container className="relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Need Custom Resources?
              </h2>
              <p className="text-xl text-white/90 mb-10 leading-relaxed">
                Our team can create tailored guides and templates specifically for your organization's 
                unique AI implementation needs.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button 
                  variant="primary"
                  size="lg" 
                  className="bg-gradient-to-r from-primary to-secondary hover:from-primary hover:to-secondary/90 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                  onClick={() => window.location.href = '/contact'}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a2 2 0 01-2-2v-6a2 2 0 012-2h2V5a2 2 0 012-2h6a2 2 0 012 2v3z" />
                  </svg>
                  Get Custom Resources
                </Button>
                
                <Button 
                  variant="secondary"
                  size="lg"
                  className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 transition-all duration-300"
                  onClick={() => window.location.href = '/resources'}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  Browse More Resources
                </Button>
              </div>
            </motion.div>
          </div>
        </Container>
      </Section>
    </Layout>
  );
};

export default GuidesPage;